import React, { useEffect, useState, useCallback } from "react";
import "./PageShops.css";
import { useNavigate } from "react-router-dom";
import WineControls from "../components/WineControls";

function PageShops() {
  const navigate = useNavigate();

  const [shops, setShops] = useState([]);
  const [filteredShops, setFilteredShops] = useState([]);
  const [nearbyShops, setNearbyShops] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState("");
  // eslint-disable-next-line no-unused-vars
  const [userCoords, setUserCoords] = useState(null);
  const [userLocation, setUserLocation] = useState("");

  /* ================= FAVORITES ================= */

  const [favoriteOutlets, setFavoriteOutlets] = useState([]);

  const mobile =
    localStorage.getItem("user_mobile") ||
    localStorage.getItem("userMobile") ||
    "";

  const FAVORITE_API =
    "https://gitstool.in/liquor/shop_app/my_favorit_vend.php";

  /* ================= FETCH FAVORITES ================= */

  useEffect(() => {
    const fetchFavoriteOutlets = async () => {
      if (!mobile) return;

      try {
        const response = await fetch(FAVORITE_API, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            action: "list",
            user_mobile: mobile,
          }),
        });

        const data = await response.json();

        console.log("Favorite Outlets Response:", data);

        let records = [];

        if (Array.isArray(data)) {
          records = data;
        } else if (Array.isArray(data?.records)) {
          records = data.records;
        } else if (Array.isArray(data?.data)) {
          records = data.data;
        }

        /*
          Store ONLY valid site IDs.
          This prevents undefined/null IDs from
          matching every shop.
        */
        const siteIds = records
          .map((fav) => fav.site_id)
          .filter(
            (id) =>
              id !== undefined &&
              id !== null &&
              String(id).trim() !== ""
          )
          .map((id) => String(id));

        console.log("Favorite Site IDs:", siteIds);

        setFavoriteOutlets(siteIds);
      } catch (err) {
        console.log("Favorite Outlets Fetch Error:", err);
        setFavoriteOutlets([]);
      }
    };

    fetchFavoriteOutlets();
  }, [mobile, FAVORITE_API]);

  /* ================= TOGGLE FAVORITE ================= */

  const toggleFavouriteOutlet = async (shop) => {
    if (!mobile) {
      return;
    }

    const siteId = String(shop.site_id);

    const isFavourite = favoriteOutlets.includes(siteId);

    try {
      /* ================= DELETE ================= */

      if (isFavourite) {
        const response = await fetch(FAVORITE_API, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            action: "delete",
            user_mobile: mobile,
            site_id: shop.site_id,
          }),
        });

        const data = await response.json();

        console.log("Delete Favourite Response:", data);

        /* Immediately turn this heart white */
        setFavoriteOutlets((prev) =>
          prev.filter((id) => id !== siteId)
        );

        return;
      }

      /* ================= ADD ================= */

      const response = await fetch(FAVORITE_API, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          action: "add",
          user_mobile: mobile,
          site_id: shop.site_id,
        }),
      });

      const data = await response.json();

      console.log("Add Favourite Response:", data);

      /* Immediately turn ONLY this heart red */
      setFavoriteOutlets((prev) => [...prev, siteId]);
    } catch (err) {
      console.log("Favourite Outlet Error:", err);
    }
  };

  /* ================= NEARBY SHOPS ================= */

  const handleNearbyShops = (data) => {
    console.log("NEARBY SHOPS RESPONSE:", data);
    setNearbyShops(data);
  };

  /* ================= FETCH SHOPS ================= */

  useEffect(() => {
    fetchShops();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const savedLocation = localStorage.getItem("userLocation");

    if (savedLocation) {
      try {
        const locationData = JSON.parse(savedLocation);

        if (locationData?.address) {
          setUserLocation(locationData.address);
        }
      } catch (error) {
        console.error("Error reading saved location:", error);
      }
    }
  }, []);

  /* ================= SEARCH FILTER ================= */

  const filterShops = useCallback(() => {
    if (searchText.trim() === "") {
      setFilteredShops(shops);
      return;
    }

    const keyword = searchText.toLowerCase();

    const result = shops.filter((shop) => {
      return (
        shop.site_name?.toLowerCase().includes(keyword) ||
        shop.group?.toLowerCase().includes(keyword) ||
        shop.shopAdd?.toLowerCase().includes(keyword)
      );
    });

    setFilteredShops(result);
  }, [searchText, shops]);

  useEffect(() => {
    filterShops();
  }, [filterShops]);

  /* ================= SORT BY DISTANCE ================= */

  useEffect(() => {
    if (
      !nearbyShops ||
      !Array.isArray(nearbyShops.shops) ||
      nearbyShops.shops.length === 0
    ) {
      return;
    }

    console.log("SORTING SHOPS BY DISTANCE...");

    const distanceMap = new Map();

    nearbyShops.shops.forEach((nearbyShop) => {
      distanceMap.set(
        String(nearbyShop.site_id),
        parseFloat(nearbyShop.distance_km)
      );
    });

    const sortedShops = [...shops].sort((a, b) => {
      const distanceA = distanceMap.get(String(a.site_id));
      const distanceB = distanceMap.get(String(b.site_id));

      if (distanceA === undefined && distanceB !== undefined) {
        return 1;
      }

      if (distanceA !== undefined && distanceB === undefined) {
        return -1;
      }

      if (distanceA === undefined && distanceB === undefined) {
        return 0;
      }

      return distanceA - distanceB;
    });

    console.log(
      "SORTED SHOPS:",
      sortedShops.map((shop) => ({
        site_id: shop.site_id,
        site_name: shop.site_name,
        distance_km: distanceMap.get(String(shop.site_id)),
      }))
    );

    setShops(sortedShops);
  }, [nearbyShops, shops]);

  /* ================= FETCH SHOPS ================= */

  const fetchShops = async () => {
    try {
      setLoading(true);

      const response = await fetch(
        "https://gitstool.in/liquor/shop_app/shop_fetch.php"
      );

      const data = await response.json();

      console.log("Shop API Response:", data);

      if (Array.isArray(data)) {
        setShops(data);
        setFilteredShops(data);
      } else {
        setShops([]);
        setFilteredShops([]);
      }
    } catch (err) {
      console.log(err);
      setShops([]);
      setFilteredShops([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    setSearchText(e.target.value);
  };

  /* ================= OPEN SHOP ================= */

  const openShop = (shop) => {
    localStorage.setItem("selected_site_id", shop.site_id);

    localStorage.setItem("selected_site_name", shop.site_name);

    localStorage.setItem("selected_shop_address", shop.shopAdd);

    navigate(
      `/shop-categories?site_id=${shop.site_id}&shop_name=${encodeURIComponent(
        shop.site_name
      )}&shop_address=${encodeURIComponent(shop.shopAdd || "")}`
    );
  };

  /* ================= LOADING ================= */

  if (loading) {
    return <div className="page-shops-loading">Loading Shops...</div>;
  }

  /* ================= UI ================= */

  return (
    <div className="page-shops">
      {/* SEARCH */}

      {userLocation && (
        <div className="shops-location-header">
          <button
            className="shops-back-button"
            onClick={() => navigate("/shop")}
            aria-label="Go back to home"
          >
            ←
          </button>

          <div className="shops-current-location">{userLocation}</div>
        </div>
      )}

      <div className="shops-search">
        <input
          type="text"
          placeholder="Search by group, shop name, address..."
          value={searchText}
          onChange={handleSearch}
        />
      </div>

      {/* LOCATION */}

      <div className="shops-location-controls">
        <WineControls
          catId="ALL"
          catName="ALL"
          categories={[]}
          selectedCatId="ALL"
          setSelectedCatId={() => {}}
          setSelectedCatName={() => {}}
          cbSearch={() => {}}
          cbChangedFilter={() => {}}
          cbChangedSort={() => {}}
          cbChangeOutputType={() => {}}
          cbSetUserCoords={setUserCoords}
          cbSetNearbyShops={handleNearbyShops}
          showLocationSearch={false}
          showSearchBar={false}
          showPriceFilter={false}
          showCategoryFilter={false}
          showShopFilterInDrawer={false}
        />
      </div>

      {/* SHOPS */}

      <div className="shops-grid">
        {filteredShops.length === 0 ? (
          <div className="no-shops">No Shops Found</div>
        ) : (
          filteredShops.map((shop) => {
            /* Check whether THIS shop is favourite */

            const isFavourite = favoriteOutlets.includes(String(shop.site_id));

            return (
              <div className="shop-card" key={shop.site_id}>
                {/* ================= FAVOURITE ================= */}

                <div
                  className={`shop-favourite ${
                    isFavourite ? "shop-favourite-active" : ""
                  }`}
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleFavouriteOutlet(shop);
                  }}
                  title={
                    isFavourite
                      ? "Remove from favourites"
                      : "Add to favourites"
                  }
                >
                  {isFavourite ? "❤️" : "🤍"}
                </div>

                {/* ================= SHOP ================= */}

                <div
                  className="shop-click-area"
                  onClick={() => openShop(shop)}
                >
                  <div className="shop-group">{shop.group}</div>

                  <div className="shop-image">
                    <img src="/images/logo.png" alt="DSIIDC Logo" />
                  </div>

                  <div className="shop-name">{shop.site_name}</div>

                  <div className="shop-address">{shop.shopAdd}</div>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

export default PageShops;